import { signOut } from "@internal/auth";
import { useState } from "react";
import { ParentLayout } from "@/components/layout/ParentLayout";
import { Avatar } from "@/components/ui/Avatar";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { useObservable } from "@/hooks/useObservable";
import { familyService } from "@/services/family.service";

const _COLORS = [
  "#2E4A3A",
  "#E2745A",
  "#E9C46A",
  "#8BBEAA",
  "#9B8EA0",
  "#F4A261",
  "#457B9D",
  "#E63946",
];

export function ParentFamily() {
  const familyState = useObservable(familyService.state$, {
    members: [],
    settings: null,
    loading: false,
  });
  const [signingOut, setSigningOut] = useState(false);

  const parents = familyState.members.filter((m) => m.data.role === "parent");
  const children = familyState.members.filter((m) => m.data.role === "child");

  async function handleSignOut() {
    setSigningOut(true);
    try {
      await signOut();
    } finally {
      setSigningOut(false);
    }
  }

  return (
    <ParentLayout>
      <div style={{ maxWidth: 600 }}>
        <div style={{ marginBottom: 28 }}>
          <div
            style={{
              fontSize: 12,
              color: "var(--ink-3)",
              textTransform: "uppercase",
              letterSpacing: 0.7,
              fontWeight: 700,
            }}
          >
            Innstillinger
          </div>
          <div
            style={{
              fontFamily: "var(--font-display)",
              fontStyle: "italic",
              fontSize: 34,
              fontWeight: 400,
              marginTop: 2,
              letterSpacing: -0.8,
            }}
          >
            Familien
          </div>
        </div>

        {familyState.settings && (
          <Card style={{ padding: 20, marginBottom: 20 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.7,
                color: "var(--ink-3)",
                marginBottom: 8,
              }}
            >
              Familienavn
            </div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>
              {familyState.settings.name}
            </div>
          </Card>
        )}

        <MemberSection title="Foreldre" members={parents} />
        <MemberSection title="Barn" members={children} />

        <div
          style={{
            marginTop: 32,
            paddingTop: 24,
            borderTop: "1px solid var(--line)",
          }}
        >
          <Button
            variant="ghost"
            size="md"
            onClick={handleSignOut}
            disabled={signingOut}
            data-testid="sign-out-button"
          >
            {signingOut ? "Logger ut…" : "Logg ut"}
          </Button>
        </div>
      </div>
    </ParentLayout>
  );
}

function MemberSection({
  title,
  members,
}: {
  title: string;
  members: ReturnType<typeof familyService.children>;
}) {
  if (!members.length) return null;
  return (
    <div style={{ marginBottom: 20 }}>
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: 0.7,
          color: "var(--ink-2)",
          marginBottom: 10,
        }}
      >
        {title}
      </div>
      <Card padding={0} style={{ overflow: "hidden" }}>
        {members.map((m, i) => (
          <div
            key={m.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              padding: "14px 20px",
              borderBottom:
                i < members.length - 1 ? "1px solid var(--line)" : "none",
            }}
          >
            <Avatar person={m} size={42} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{m.data.name}</div>
              <div style={{ fontSize: 12, color: "var(--ink-3)" }}>
                {m.data.age} år
              </div>
            </div>
            <div
              style={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                background: m.data.color,
              }}
            />
          </div>
        ))}
      </Card>
    </div>
  );
}
